import { Form, type FormRef, PREDEFINED_FORMS } from '@atlas/react-forms';
import cx from 'classnames';
import { type FC, useCallback, useMemo, useRef, useState } from 'react';

import {
  type PaymentMethod,
  PaymentMethodType,
  type TransactionResponse,
  createDepositRequest,
  createWithdrawalRequest,
} from 'api/payments';
import fields from 'components/Form/fields';
import Icon from 'components/Icon/Icon';
import { useClientProfile } from 'store';
import { getMediaUrl } from 'utils/assets';
import { Translation } from 'utils/translations';
import { LoadingState } from 'utils/types';
import useIntl from 'utils/useIntl';

import ContainerComponent from './ContainerComponent';
import {
  type PaymentError,
  useErrorNotification,
} from './useErrorNotification';
import { useSuccessNotification } from './useSuccessNotification';

import styles from './PaymentForm.module.scss';

type PaymentFormProps = {
  method: PaymentMethodType;
  selectedPayment: PaymentMethod;
  className?: string;
};

const PaymentForm: FC<PaymentFormProps> = ({
  method,
  selectedPayment,
  className,
}) => {
  const [{ sessionToken }] = useClientProfile();
  const { formatMoney } = useIntl();
  const [loadingState, setLoadingState] = useState(LoadingState.initial);
  const [fieldValue, setFieldValue] = useState<number | undefined>(undefined);
  const paymentMethod = method;
  const formRef = useRef<FormRef>(null);
  const form =
    selectedPayment &&
    (paymentMethod === PaymentMethodType.DEPOSIT
      ? PREDEFINED_FORMS.depositForm(selectedPayment)
      : PREDEFINED_FORMS.withdrawalForm(selectedPayment));
  const imageLink = selectedPayment.desktopImageLink;
  const fullImageLink = imageLink ? getMediaUrl(imageLink) : undefined;
  const requestAction =
    paymentMethod === PaymentMethodType.DEPOSIT
      ? createDepositRequest
      : createWithdrawalRequest;

  const paymentAction = useCallback(
    async ({ amount }: { amount: number }) => {
      const integerAmount = Math.round(amount * 100);

      setLoadingState(LoadingState.loading);

      return await requestAction({
        body: {
          amount: integerAmount,
          pmId: selectedPayment.id.toString(),
        },
        headers: {
          authorization: `Bearer ${sessionToken}`,
        },
      });
    },
    [sessionToken, selectedPayment.id, requestAction],
  );

  const memoizedData = useMemo(
    () => ({
      imageSources: {
        [paymentMethod]: fullImageLink,
      },
      paymentMethod,
    }),
    [fullImageLink, paymentMethod],
  );

  const successNotify = useSuccessNotification(memoizedData);
  const errorNotify = useErrorNotification();

  const handleSubmitResult = (paymentData: TransactionResponse) => {
    successNotify(paymentData);
    setLoadingState(LoadingState.success);
  };

  const handleErrorResult = (error: PaymentError) => {
    errorNotify(error);
    setLoadingState(LoadingState.failed);
  };

  const isDisabledSubmitButton =
    loadingState === LoadingState.loading || !fieldValue;

  const handleSelectDepositAmount = useCallback((amount: number): void => {
    if (formRef.current) {
      formRef.current?.setFieldValueHandler('amount', amount, false);
    }
  }, []);

  return (
    <div className={cx(className, styles['page-form'])}>
      <div className={styles['form-header']}>
        <div className={styles['form-title']}>
          <span className={styles['form-text']}>
            <Translation
              message={`${method.toLowerCase()}_with`}
              params={{ name: '' }} // fix for breaking selectedPayment name to new line
            />
            <span className={styles['selected-payment-name']}>
              {selectedPayment.name}
            </span>
          </span>
          {fullImageLink ? (
            <img
              className={styles['form-image']}
              src={fullImageLink}
              alt={selectedPayment.name}
            />
          ) : (
            <Icon
              width={30}
              height={30}
              className={styles.icon}
              name="payment-methods"
            />
          )}
        </div>
        <div className={styles['form-amount']}>
          <span>
            <Translation message="profile_min_amount" />
            :&nbsp;
            {formatMoney(selectedPayment.minAmount)}
          </span>
          <span>
            <Translation message="profile_max_amount" />
            :&nbsp;
            {formatMoney(selectedPayment.maxAmount)}
          </span>
        </div>
      </div>
      {form && (
        <Form
          ref={formRef}
          form={form}
          fieldComponents={fields}
          onFieldValueChange={(field) => setFieldValue(Number(field.value))}
          // @ts-ignore
          action={paymentAction}
          ContainerComponent={ContainerComponent}
          ContainerComponentProps={{
            /**
             * additional props for container component
             */
            isDisabledSubmitButton,
            handleSelectDepositAmount,
            fieldValue,
            method,
          }}
          // @ts-ignore
          onSubmit={handleSubmitResult}
          // @ts-ignore
          onError={handleErrorResult}
        />
      )}
    </div>
  );
};

export default PaymentForm;
